import {
  Badge, Button, Card, DateInput, ErrorState, Field, Input, LoadingState, Permissions, PhoneInput, Row, Stack,
  invalidDateIn, normalisePhone, useApi, useI18n, usePermission,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Who besides the guardians may collect a child (FR-CHD-09), kept by the nursery alone (Q13, Q43).
 *
 * A family tells the nursery, and someone holding `children.update` records it here. Every guardian may
 * already collect (Q42), so this list is the others: a grandparent, a driver, an aunt for one week.
 */
export function PickupsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatPhone, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const canUpdate = usePermission(Permissions.children.update);
  const [adding, setAdding] = useState(false);

  const key = ['pickups', childId];
  const pickups = useQuery({ queryKey: key, queryFn: () => api.pickups.list(childId) });

  const remove = useMutation({
    mutationFn: (pickupId: number) => api.pickups.remove(childId, pickupId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  return (
    <Card
      title={t('pickups.title')}
      hint={t('pickups.hint')}
      action={canUpdate && !adding
        ? <Button variant="secondary" onClick={() => setAdding(true)}>{t('pickups.add')}</Button>
        : undefined}
    >
      <Stack>
        {adding && (
          <AddPickupForm
            childId={childId}
            onCancel={() => setAdding(false)}
            onAdded={() => {
              setAdding(false);
              void queryClient.invalidateQueries({ queryKey: key });
            }}
          />
        )}

        {remove.isError && <ErrorState error={remove.error} />}
        {pickups.isPending && <LoadingState />}
        {pickups.isError && <ErrorState error={pickups.error} onRetry={() => void pickups.refetch()} />}
        {pickups.data?.items.length === 0 && <p className="dg-card__hint">{t('pickups.empty')}</p>}

        {pickups.data?.items.map((pickup) => (
          <Row key={pickup.id} between>
            <span className="dg-stack dg-stack--tight">
              <strong>{pickup.fullName}</strong>
              <span className="dg-row">
                {pickup.relationship && <span className="dg-card__hint">{pickup.relationship}</span>}
                {(pickup.validFrom || pickup.validTo) && (
                  <span className="dg-card__hint">
                    {t('pickups.validity', {
                      from: pickup.validFrom ? formatDate(pickup.validFrom) : '—',
                      to: pickup.validTo ? formatDate(pickup.validTo) : '—',
                    })}
                  </span>
                )}
                {!pickup.isValidToday && <Badge tone="neutral">{t('pickups.expired')}</Badge>}
              </span>
            </span>
            <span className="dg-row">
              <a href={`tel:${pickup.phone}`} dir="ltr">{formatPhone(pickup.phone)}</a>
              {canUpdate && (
                <Button
                  variant="ghost"
                  busy={remove.isPending && remove.variables === pickup.id}
                  onClick={() => remove.mutate(pickup.id)}
                >
                  {t('pickups.remove')}
                </Button>
              )}
            </span>
          </Row>
        ))}
      </Stack>
    </Card>
  );
}

function AddPickupForm({ childId, onCancel, onAdded }: {
  childId: number;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [relationship, setRelationship] = useState('');
  const [idDocumentRef, setIdDocumentRef] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [errors, setErrors] = useState<{ fullName?: string; phone?: string }>({});

  const add = useMutation({
    mutationFn: (normalised: string) => api.pickups.add(childId, {
      fullName: fullName.trim(),
      phone: normalised,
      relationship: relationship.trim() || null,
      idDocumentRef: idDocumentRef.trim() || null,
      validFrom: validFrom || null,
      validTo: validTo || null,
    }),
    onSuccess: onAdded,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const unreadable = invalidDateIn(event.currentTarget);
    if (unreadable) { unreadable.focus(); return; }
    const normalised = normalisePhone(phone);
    setErrors({
      ...(fullName.trim() ? {} : { fullName: t('pickups.name.required') }),
      ...(normalised ? {} : { phone: t('auth.phone.required') }),
    });
    if (normalised && fullName.trim()) add.mutate(normalised);
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {add.isError && <ErrorState error={add.error} />}

        <Field label={t('pickups.name')} error={errors.fullName} required>
          {(props) => <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} autoFocus />}
        </Field>

        <Field label={t('auth.phone')} hint={t('auth.phone.hint')} error={errors.phone} required>
          {(props) => <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />}
        </Field>

        <Field label={t('pickups.relationship')}>
          {(props) => <Input {...props} value={relationship} onChange={(event) => setRelationship(event.target.value)} />}
        </Field>

        <Field label={t('pickups.idDocument')}>
          {(props) => <Input {...props} value={idDocumentRef} onChange={(event) => setIdDocumentRef(event.target.value)} />}
        </Field>

        <Row>
          <Field label={t('pickups.validFrom')}>
            {(props) => <DateInput {...props} value={validFrom} onChange={setValidFrom} />}
          </Field>
          <Field label={t('pickups.validTo')}>
            {(props) => <DateInput {...props} value={validTo} onChange={setValidTo} />}
          </Field>
        </Row>

        <Row>
          <Button type="submit" busy={add.isPending} busyLabel={t('action.saving')}>{t('pickups.add')}</Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}
